import {
  EnvironmentInjector,
  Injector,
  WritableSignal,
  signal,
} from '@angular/core';
import { FieldTree, form } from '@angular/forms/signals';
import {
  FieldRules,
  FieldShape,
  NO_RULES,
  fieldRulesSchema,
} from './field-rules';

export interface FieldEntry<T = unknown> {
  readonly tree: FieldTree<T>;
  readonly model: WritableSignal<T>;
  readonly rules: WritableSignal<FieldRules>;
  readonly locked: WritableSignal<boolean>;
}

export function createFieldEntry<T>(
  value: T,
  injector: Injector,
  shape: FieldShape = 'scalar',
  rules: WritableSignal<FieldRules> = signal(NO_RULES)
): FieldEntry<T> {
  const model = signal(value);
  const locked = signal(false);
  const tree = form(model, fieldRulesSchema<T>(rules, shape, locked), {
    injector,
  });
  return { tree, model, rules, locked };
}

/**
 * One item build's fields, keyed by field id. Owns the injector its trees' effects run on, so
 * destroying the set stops them.
 */
export class TaskFieldSet {
  static readonly EMPTY = new TaskFieldSet(null, new Map());

  constructor(
    private readonly owner: EnvironmentInjector | null,
    private readonly byId: ReadonlyMap<string, FieldEntry>
  ) {}

  get injector(): Injector {
    if (!this.owner) throw new Error('An empty field set has no injector.');
    return this.owner;
  }

  get(id: string): FieldEntry | undefined {
    return this.byId.get(id);
  }

  ids(): string[] {
    return Array.from(this.byId.keys());
  }

  entries(): FieldEntry[] {
    return Array.from(this.byId.values());
  }

  markAllAsTouched(): void {
    for (const entry of this.byId.values()) entry.tree().markAsTouched();
  }

  destroy(): void {
    this.owner?.destroy();
  }
}
